import { DEFAULT_MCPJAM_CLIENT_ID_METADATA_URL } from "./client-identity.js";
import {
  canonicalizeResourceUrl,
  evaluateResourceIndicator,
  type ResourceIndicatorDecision,
} from "./resource-policy.js";
import { resolvePreregisteredClientAuthMethod } from "./state-machines/shared/client-auth.js";
import type {
  OAuthAuthMode,
  OAuthProtocolVersion,
  RegistrationStrategy2025_03_26,
  RegistrationStrategy2025_06_18,
  RegistrationStrategy2025_11_25,
} from "./state-machines/types.js";

/** @deprecated Use {@link RegistrationStrategy} from `@mcpjam/sdk` — the
 * shared vocabulary for OAuth and XAA. Structurally identical. */
export type OAuthRegistrationStrategy =
  | RegistrationStrategy2025_03_26
  | RegistrationStrategy2025_06_18
  | RegistrationStrategy2025_11_25;

export type OAuthProtocolMode = "auto" | OAuthProtocolVersion;
/** @deprecated Use {@link RegistrationMode} from `@mcpjam/sdk` — the shared
 * vocabulary for OAuth and XAA. Structurally identical. */
export type OAuthRegistrationMode = "auto" | OAuthRegistrationStrategy;

export interface AuthorizationDiscoverySnapshot {
  registrationStrategies?: Array<"preregistered" | "dcr" | "cimd">;
  authorizationServerMetadataUrl?: string;
  authorizationServerMetadata?: Record<string, unknown>;
  resourceMetadataUrl?: string;
  resourceMetadata?: Record<string, unknown>;
  discoveryError?: string;
}

export interface AuthorizationPlanInput {
  serverUrl?: string;
  protocolMode?: OAuthProtocolMode;
  protocolVersion?: OAuthProtocolVersion;
  registrationMode?: OAuthRegistrationMode;
  registrationStrategy?: OAuthRegistrationStrategy;
  clientId?: string;
  clientSecret?: string;
  hasClientSecret?: boolean;
  clientIdMetadataUrl?: string;
  useRegistryOAuthProxy?: boolean;
  authMode?: OAuthAuthMode;
  discovery?: AuthorizationDiscoverySnapshot;
}

export interface AuthorizationPlanCapabilities {
  registrationStrategies: Array<"preregistered" | "dcr" | "cimd">;
  supportsCimd: boolean;
  supportsDcr: boolean;
  authorizationServerMetadataUrl?: string;
  resourceMetadataUrl?: string;
  discoveryError?: string;
}

export type AuthorizationPlanBlockerCode =
  | "PREREGISTERED_MISSING_CLIENT_ID"
  | "PREREGISTERED_MISSING_CLIENT_SECRET"
  | "CIMD_UNSUPPORTED_PROTOCOL"
  | "CIMD_UNSUPPORTED_AUTH_MODE"
  | "CIMD_NOT_ADVERTISED"
  | "DCR_NOT_ADVERTISED"
  | "AUTO_INCOMPLETE_PREREGISTERED_CREDENTIALS"
  | "AUTO_NO_CLIENT_CREDENTIALS_COMPATIBLE_FLOW"
  | "AUTO_NO_USABLE_REGISTRATION_FLOW";

export interface AuthorizationPlanBlocker {
  code: AuthorizationPlanBlockerCode;
  message: string;
}

export interface ResolvedAuthorizationPlan {
  protocolMode: OAuthProtocolMode;
  protocolVersion: OAuthProtocolVersion;
  registrationMode: OAuthRegistrationMode;
  registrationStrategy?: OAuthRegistrationStrategy;
  status: "ready" | "discovery_required" | "blocked";
  blockerDetails: AuthorizationPlanBlocker[];
  blockers: string[];
  warnings: string[];
  capabilities: AuthorizationPlanCapabilities;
  // Always the canonicalized server URL (kept stable for existing consumers).
  canonicalResource?: string;
  // The resolved resource-indicator decision, present only when a discovery
  // snapshot with Protected Resource Metadata was provided. This is the value
  // the flow will actually send; `canonicalResource` remains the
  // server-URL-derived best guess.
  resourceIndicator?: ResourceIndicatorDecision;
  clientIdMetadataUrl?: string;
  summary: string;
}

function normalizeProtocolMode(
  input: Pick<AuthorizationPlanInput, "protocolMode" | "protocolVersion">,
): OAuthProtocolMode {
  if (input.protocolMode) {
    return input.protocolMode;
  }

  return input.protocolVersion ?? "auto";
}

function normalizeRegistrationMode(
  input: Pick<
    AuthorizationPlanInput,
    "registrationMode" | "registrationStrategy"
  >,
): OAuthRegistrationMode {
  if (input.registrationMode) {
    return input.registrationMode;
  }

  return input.registrationStrategy ?? "auto";
}

function resolveProtocolVersion(
  input: Pick<AuthorizationPlanInput, "protocolMode" | "protocolVersion">,
): OAuthProtocolVersion {
  if (input.protocolVersion) {
    return input.protocolVersion;
  }

  if (input.protocolMode && input.protocolMode !== "auto") {
    return input.protocolMode;
  }

  return "2025-11-25";
}

function normalizeClientIdMetadataUrl(url?: string): string | undefined {
  const trimmed = url?.trim();
  return trimmed ? trimmed : undefined;
}

function summarizePlan(
  status: ResolvedAuthorizationPlan["status"],
  registrationStrategy: OAuthRegistrationStrategy | undefined,
  blockers: string[],
  warnings: string[],
  capabilities: AuthorizationPlanCapabilities,
): string {
  if (status === "blocked") {
    return blockers[0] ?? "OAuth authorization is blocked.";
  }

  if (status === "discovery_required") {
    return "Automatic discovery will choose pre-registered credentials, CIMD, or DCR after the server is probed.";
  }

  if (registrationStrategy === "preregistered") {
    return "Using pre-registered client credentials.";
  }

  if (registrationStrategy === "cimd") {
    return "Automatic discovery resolved to Client ID Metadata Documents (CIMD).";
  }

  if (registrationStrategy === "dcr") {
    return "Automatic discovery resolved to Dynamic Client Registration (DCR).";
  }

  if (warnings.length > 0) {
    return warnings[0];
  }

  if (capabilities.registrationStrategies.length > 0) {
    return `Supported strategies: ${capabilities.registrationStrategies.join(", ")}.`;
  }

  return "OAuth authorization is ready.";
}

export function resolveRegistrationStrategies(
  protocolVersion: OAuthProtocolVersion,
  authServerMetadata: Record<string, unknown> | undefined,
): Array<"preregistered" | "dcr" | "cimd"> {
  const strategies: Array<"preregistered" | "dcr" | "cimd"> = [
    "preregistered",
  ];

  if (authServerMetadata?.registration_endpoint) {
    strategies.push("dcr");
  }

  if (
    (protocolVersion === "2025-11-25" || protocolVersion === "2026-07-28") &&
    authServerMetadata?.client_id_metadata_document_supported === true
  ) {
    strategies.push("cimd");
  }

  return strategies;
}

function buildCapabilities(
  protocolVersion: OAuthProtocolVersion,
  discovery?: AuthorizationDiscoverySnapshot,
): AuthorizationPlanCapabilities {
  const registrationStrategies =
    discovery?.registrationStrategies ??
    resolveRegistrationStrategies(
      protocolVersion,
      discovery?.authorizationServerMetadata,
    );

  return {
    registrationStrategies,
    supportsCimd: registrationStrategies.includes("cimd"),
    supportsDcr: registrationStrategies.includes("dcr"),
    authorizationServerMetadataUrl: discovery?.authorizationServerMetadataUrl,
    resourceMetadataUrl: discovery?.resourceMetadataUrl,
    discoveryError: discovery?.discoveryError,
  };
}

export function resolveAuthorizationPlan(
  input: AuthorizationPlanInput,
): ResolvedAuthorizationPlan {
  const protocolMode = normalizeProtocolMode(input);
  const protocolVersion = resolveProtocolVersion(input);
  const registrationMode = normalizeRegistrationMode(input);
  const blockerDetails: AuthorizationPlanBlocker[] = [];
  const blockers: string[] = [];
  const warnings: string[] = [];
  const capabilities = buildCapabilities(protocolVersion, input.discovery);
  const hasDiscovery = input.discovery !== undefined;
  const authMode = input.authMode ?? "interactive";
  const trimmedClientId = input.clientId?.trim();
  const trimmedClientSecret = input.clientSecret?.trim();
  const hasClientSecret = Boolean(trimmedClientSecret || input.hasClientSecret);
  const hasAnyPreregisteredCredentialInput = Boolean(
    input.useRegistryOAuthProxy ||
      trimmedClientId ||
      trimmedClientSecret ||
      input.hasClientSecret,
  );
  const hasCompletePreregisteredCredentials = Boolean(
    input.useRegistryOAuthProxy ||
      (authMode === "client_credentials"
        ? trimmedClientId && hasClientSecret
        : trimmedClientId),
  );
  const hasIncompletePreregisteredCredentials =
    hasAnyPreregisteredCredentialInput && !hasCompletePreregisteredCredentials;
  const clientIdMetadataUrl =
    normalizeClientIdMetadataUrl(input.clientIdMetadataUrl) ??
    DEFAULT_MCPJAM_CLIENT_ID_METADATA_URL;
  const preregisteredAutoModeBlocker =
    authMode === "client_credentials"
      ? "Automatic OAuth found incomplete pre-registered credentials. Provide both a client ID and client secret, or clear the partial credentials to use discovery."
      : "Automatic OAuth found incomplete pre-registered credentials. Provide a client ID, or clear the partial credentials to use discovery.";

  let status: ResolvedAuthorizationPlan["status"] = "ready";
  let registrationStrategy: OAuthRegistrationStrategy | undefined;
  const pushBlocker = (
    code: AuthorizationPlanBlockerCode,
    message: string,
  ) => {
    blockerDetails.push({ code, message });
    blockers.push(message);
  };

  if (capabilities.discoveryError) {
    warnings.push(capabilities.discoveryError);
  }

  if (registrationMode === "preregistered") {
    registrationStrategy = "preregistered";
    if (!input.useRegistryOAuthProxy && !trimmedClientId) {
      pushBlocker(
        "PREREGISTERED_MISSING_CLIENT_ID",
        "Pre-registered OAuth requires a client ID before the flow can start.",
      );
    }
    if (authMode === "client_credentials" && !hasClientSecret) {
      pushBlocker(
        "PREREGISTERED_MISSING_CLIENT_SECRET",
        "Client credentials mode requires a client secret for pre-registered OAuth.",
      );
    }
  } else if (registrationMode === "cimd") {
    registrationStrategy = "cimd";
    if (protocolVersion !== "2025-11-25" && protocolVersion !== "2026-07-28") {
      pushBlocker(
        "CIMD_UNSUPPORTED_PROTOCOL",
        `CIMD registration is not supported for protocol version ${protocolVersion}.`,
      );
    }
    if (authMode === "client_credentials") {
      pushBlocker(
        "CIMD_UNSUPPORTED_AUTH_MODE",
        "Client credentials mode cannot use Client ID Metadata Documents (CIMD).",
      );
    }
    if (hasDiscovery && !capabilities.supportsCimd) {
      pushBlocker(
        "CIMD_NOT_ADVERTISED",
        "The authorization server did not advertise Client ID Metadata Document support.",
      );
    }
  } else if (registrationMode === "dcr") {
    registrationStrategy = "dcr";
    if (hasDiscovery && !capabilities.supportsDcr) {
      pushBlocker(
        "DCR_NOT_ADVERTISED",
        "The authorization server did not advertise a registration_endpoint required for DCR.",
      );
    }
  } else if (hasIncompletePreregisteredCredentials) {
    pushBlocker(
      "AUTO_INCOMPLETE_PREREGISTERED_CREDENTIALS",
      preregisteredAutoModeBlocker,
    );
  } else if (hasCompletePreregisteredCredentials) {
    registrationStrategy = "preregistered";
  } else if (!hasDiscovery) {
    status = "discovery_required";
  } else if (authMode !== "client_credentials" && capabilities.supportsCimd) {
    registrationStrategy = "cimd";
  } else if (capabilities.supportsDcr) {
    registrationStrategy = "dcr";
  } else {
    pushBlocker(
      authMode === "client_credentials"
        ? "AUTO_NO_CLIENT_CREDENTIALS_COMPATIBLE_FLOW"
        : "AUTO_NO_USABLE_REGISTRATION_FLOW",
      authMode === "client_credentials"
        ? "Automatic OAuth could not find a client_credentials-compatible flow. Configure pre-registered credentials or use a server that supports DCR."
        : "Automatic OAuth could not find a usable CIMD or DCR flow. Configure pre-registered credentials to continue.",
    );
  }

  if (blockers.length > 0) {
    status = "blocked";
  }

  if (
    status !== "blocked" &&
    registrationStrategy === "preregistered" &&
    hasDiscovery &&
    !input.useRegistryOAuthProxy
  ) {
    const clientAuthMethod = resolvePreregisteredClientAuthMethod({
      authorizationServerMetadata: input.discovery?.authorizationServerMetadata,
      hasClientSecret,
    });
    if (!clientAuthMethod.ok) {
      pushBlocker("PREREGISTERED_MISSING_CLIENT_SECRET", clientAuthMethod.message);
      status = "blocked";
    }
  }

  const plan: ResolvedAuthorizationPlan = {
    protocolMode,
    protocolVersion,
    registrationMode,
    ...(registrationStrategy ? { registrationStrategy } : {}),
    status,
    blockerDetails,
    blockers,
    warnings,
    capabilities,
    ...(input.serverUrl
      ? { canonicalResource: canonicalizeResourceUrl(input.serverUrl) }
      : {}),
    ...(input.serverUrl &&
    typeof input.discovery?.resourceMetadata?.resource === "string"
      ? {
          resourceIndicator: evaluateResourceIndicator({
            serverUrl: input.serverUrl,
            prmResource: input.discovery.resourceMetadata.resource,
          }),
        }
      : {}),
    ...((registrationStrategy === "cimd" ||
      registrationMode === "cimd") && clientIdMetadataUrl
      ? { clientIdMetadataUrl }
      : {}),
    summary: "",
  };

  plan.summary = summarizePlan(
    plan.status,
    plan.registrationStrategy,
    blockers,
    warnings,
    capabilities,
  );

  return plan;
}
